import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import orderBy from 'lodash/fp/orderBy';

import {
  ApolloGqlTalentForTalentBoard,
  TalentBoardEventType,
  TalentBoardJobFunnelPhase,
} from '@bloomays-lib/types.shared';
import { ProfileCard, theme } from '@bloomays-lib/ui.shared';
import { getPhaseColor } from '../../helpers/utils';
import { Logger } from '../../services/service-logger';

const logger = Logger('JobFunnelColumn');

export type JobFunnelColumnProps = {
  jobId: string;
  jobName: string;
  phase: TalentBoardJobFunnelPhase;
  talents: ApolloGqlTalentForTalentBoard[];
  onTalentClick: (talent: ApolloGqlTalentForTalentBoard) => void;
  haveFilters?: boolean;
  onResetFilters?: () => void;
};

const JobFunnelColumn = ({
  jobId,
  jobName,
  phase,
  talents,
  onTalentClick,
  haveFilters,
  onResetFilters,
}: JobFunnelColumnProps) => {
  const [hideTalents, setHideTalents] = useState(false);

  const { t } = useTranslation('talentBoard');

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const sortedTalents = useMemo(() => {
    return orderBy((talent: ApolloGqlTalentForTalentBoard) => talent.topProfile, ['desc'])(talents);
  }, [talents]);

  const handleVisible = () => {
    if (isMobile) {
      setHideTalents(!hideTalents);
    }
  };

  const handleResetFilters = (event: React.MouseEvent) => {
    event.stopPropagation();
    logger.info('Traffic', {
      page: 'TalentBoard',
      evt: { name: TalentBoardEventType.TalentboardResetFilters },
      job: {
        id: jobId,
        name: jobName,
      },
    });
    onResetFilters && onResetFilters();
  };

  return (
    <ColumnContainer>
      <TitleCell color={getPhaseColor(phase)} onClick={handleVisible}>
        <TitleContent>
          <Typography variant="body1Regular">
            {t(`jobFunnelPhases.${phase}`)} {talents.length > 0 ? `(${talents.length})` : ''}
          </Typography>
          {haveFilters && (
            <FilterAltOffIcon
              sx={{ fontSize: '20px', marginLeft: '8px', cursor: 'pointer' }}
              onClick={handleResetFilters}
            />
          )}
        </TitleContent>
      </TitleCell>
      {(!isMobile || !hideTalents) &&
        sortedTalents.map((talent) => (
          <ProfileCard
            activeHover={true}
            key={talent.id}
            name={`${talent.firstname} ${talent.lastname}`}
            jobTitle={talent.jobTitle}
            image={talent.image || ''}
            size="medium"
            onClick={() => {
              onTalentClick(talent);
            }}
            notSeen={!talent.markedAsSeen}
            topProfile={talent.topProfile}
            becameBloomer={talent.becameBloomer}
            caption={talent.cardTamComment || ''}
            commented={talent.comments.length > 0}
          />
        ))}
    </ColumnContainer>
  );
};

const ColumnContainer = styled('div')(({ theme }) => ({
  borderRadius: '10px',
  backgroundColor: theme.palette.neutral[10],
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  padding: '10px',
  width: '100%',
}));

const TitleCell = styled('div')(({ color, theme }) => ({
  width: '100%',
  padding: '5px',
  borderRadius: '5px',
  textAlign: 'center',
  backgroundColor: color,
  marginBottom: '5px',
}));

const TitleContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export default JobFunnelColumn;
